/* ═══════════════════════════════════════════════════════════════
   FormaService — Standard Configs + Custom Tokens via EC2 API (cloud, in-memory cache)
   ═══════════════════════════════════════════════════════════════ */

import type { StandardConfig } from '../../shared/types';
import * as cache from './cloudCache';
import type { TableOpts } from './cloudCache';

const formaOpts: TableOpts = {
  jsonFields: [
    'docSettings',
    'levels',
    'headerFooter',
    'special',
    'images',
    'editHistory',
    'placeholders',
    'bindings',
  ],
};

const tokenOpts: TableOpts = {};

export interface CustomTokenRecord {
  id: string;
  tokens: string[];
}

export const FormaService = {
  /* ── Formas (Standard Configs) ── */
  list: async (): Promise<StandardConfig[]> =>
    cache.list<StandardConfig>('standard_configs', formaOpts),

  save: async (cfg: StandardConfig): Promise<StandardConfig> =>
    cache.save('standard_configs', cfg, formaOpts),

  delete: async (id: string): Promise<void> => cache.remove('standard_configs', id),

  /**
   * Specs-style application: applying a master forma to a project makes a
   * COPY that lives in the project — edits there touch only that copy, the
   * master stays a pristine template. Reuses the project's existing copy of
   * the same master (idempotent); no-op when handed a project forma already.
   */
  ensureProjectCopy: async (
    master: StandardConfig,
    project: { id: string; number?: string; name?: string }
  ): Promise<StandardConfig> => {
    if ((master as any).projectId) return master; // already project-scoped
    const all = await cache.list<StandardConfig>('standard_configs', formaOpts);
    const existing = all.find(
      (f: any) => f.projectId === project.id && !f.specId && f.sourceFormaId === master.id
    );
    if (existing) return existing;
    const copy: any = JSON.parse(JSON.stringify(master));
    copy.id = 'pf-' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
    copy.projectId = project.id;
    copy.sourceFormaId = master.id;
    delete copy.specId;
    delete copy.editHistory;
    delete copy.attachedProjectId; // attachment is master metadata, not the copy's
    // The copy keeps the master's {P#} blanks AND their memories; what each one
    // MEANS is a project decision, so bindings start empty and the export gate
    // holds until someone fills them in (Project Tokens palette).
    copy.bindings = copy.bindings || {};
    const baseName = String(copy.docSettings?.standardName || 'Forma').replace(/ — .*$/, '');
    copy.docSettings = {
      ...copy.docSettings,
      standardName: `${baseName} — ${project.number || project.name || 'project'}`,
    };
    await cache.save('standard_configs', copy, formaOpts);
    return copy as StandardConfig;
  },

  /* ── Custom Tokens ── */
  loadTokens: async (): Promise<string[]> => {
    const rows = await cache.list<CustomTokenRecord>('custom_tokens', tokenOpts);
    if (rows.length > 0) return rows[0].tokens || [];
    return [];
  },

  saveTokens: async (tokens: string[]): Promise<void> => {
    const record: CustomTokenRecord = {
      id: 'firm-tokens',
      tokens,
    };
    await cache.save('custom_tokens', record, { jsonFields: ['tokens'] });
  },
};
